import { useState, useEffect, type ReactNode } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import {
  Shield,
  Lock,
  ArrowRight,
  AlertTriangle,
  Clock,
  CheckCircle,
  XCircle,
  Database,
  Users,
  Hash,
  Twitter,
  Check,
} from 'lucide-react'

const API_BASE =
  window.location.hostname !== 'localhost'
    ? `${window.location.protocol}//${window.location.hostname}/api`
    : 'http://localhost:8000'

type Module = 'x' | 'social' | 'breach'

interface XProfileData {
  name: string
  username: string
  bio: string
  followers: number
  following: number
  verified: boolean
  account_created: string
  tweet_count: number
}

interface SocialData {
  keyword: string
  total_posts: number
  platforms: string[]
  top_hashtags: string[]
  sentiment: { positive: number; negative: number; neutral: number }
}

interface BreachData {
  email: string
  breach_count: number
  total_records: number
  data_types: string[]
  breach_count_hidden: number
}

interface PreviewData {
  preview_id: string
  module: Module
  summary: string
  x_profile?: XProfileData
  social?: SocialData
  breach?: BreachData
  expires_at: string
}

function LockedRow({ label }: { label: string }) {
  return (
    <div className="flex items-center gap-3 p-3 bg-gray-800/30 border border-gray-700/40 rounded-lg">
      <Lock className="w-3.5 h-3.5 text-gray-600 flex-shrink-0" />
      <span className="text-sm text-gray-500 blur-sm select-none">{label}</span>
      <div className="ml-auto">
        <Lock className="w-3.5 h-3.5 text-gray-600" />
      </div>
    </div>
  )
}

function SkeletonBlock({ className }: { className?: string }) {
  return <div className={`bg-gray-800/60 rounded animate-pulse ${className ?? ''}`} />
}

function XProfilePanel({ data }: { data: XProfileData }) {
  return (
    <div className="space-y-4">
      {/* Profile header */}
      <div className="flex items-start gap-4">
        <div className="w-16 h-16 rounded-full bg-gray-700 border-2 border-gray-600 flex items-center justify-center flex-shrink-0">
          <Twitter className="w-7 h-7 text-gray-500" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="text-white font-semibold text-base">{data.name}</h3>
            {data.verified ? (
              <CheckCircle className="w-4 h-4 text-blue-400" />
            ) : (
              <XCircle className="w-4 h-4 text-gray-600" />
            )}
          </div>
          <p className="text-gray-400 text-sm">@{data.username}</p>
          <p className="text-gray-300 text-sm mt-1 leading-snug">{data.bio}</p>
        </div>
      </div>

      {/* Stats grid */}
      <div className="grid grid-cols-2 gap-3">
        {[
          { label: 'Followers', value: data.followers.toLocaleString() },
          { label: 'Following', value: data.following.toLocaleString() },
          { label: 'Tweets', value: data.tweet_count.toLocaleString() },
          { label: 'Account created', value: data.account_created },
        ].map((s) => (
          <div key={s.label} className="p-3 bg-gray-800/40 border border-gray-700/50 rounded-lg">
            <p className="text-xs text-gray-500 mb-0.5">{s.label}</p>
            <p className="text-white font-semibold text-sm">{s.value}</p>
          </div>
        ))}
      </div>

      {/* Locked section */}
      <div className="space-y-2 pt-2">
        <p className="text-xs text-gray-500 uppercase tracking-widest font-mono">Locked analysis</p>
        <LockedRow label="Tweet sentiment analysis across 3,200 posts" />
        <LockedRow label="Follower network graph + influence score" />
        <LockedRow label="Most used hashtags and mention clusters" />
        <LockedRow label="Posting time patterns and engagement rate" />
      </div>
    </div>
  )
}

function SocialPanel({ data }: { data: SocialData }) {
  const { positive, negative, neutral } = data.sentiment
  const total = positive + negative + neutral || 1

  return (
    <div className="space-y-4">
      {/* Overview */}
      <div className="grid grid-cols-2 gap-3">
        <div className="p-3 bg-gray-800/40 border border-gray-700/50 rounded-lg">
          <p className="text-xs text-gray-500 mb-0.5">Topic</p>
          <p className="text-white font-semibold text-sm truncate">"{data.keyword}"</p>
        </div>
        <div className="p-3 bg-gray-800/40 border border-gray-700/50 rounded-lg">
          <p className="text-xs text-gray-500 mb-0.5">Total posts found</p>
          <p className="text-white font-semibold text-sm">{data.total_posts.toLocaleString()}</p>
        </div>
      </div>

      {/* Platforms */}
      <div className="p-3 bg-gray-800/40 border border-gray-700/50 rounded-lg">
        <p className="text-xs text-gray-500 mb-1.5">Platforms</p>
        <div className="flex gap-2 flex-wrap">
          {data.platforms.map((p) => (
            <span key={p} className="px-2 py-0.5 bg-gray-700 rounded text-xs text-gray-300 capitalize">
              {p}
            </span>
          ))}
        </div>
      </div>

      {/* Hashtags */}
      <div className="p-3 bg-gray-800/40 border border-gray-700/50 rounded-lg">
        <p className="text-xs text-gray-500 mb-1.5 flex items-center gap-1">
          <Hash className="w-3 h-3" /> Top hashtags
        </p>
        <div className="flex gap-2 flex-wrap">
          {data.top_hashtags.map((tag) => (
            <span key={tag} className="px-2 py-0.5 bg-blue-500/10 border border-blue-500/20 rounded text-xs text-blue-300">
              #{tag}
            </span>
          ))}
        </div>
      </div>

      {/* Sentiment bar */}
      <div className="p-3 bg-gray-800/40 border border-gray-700/50 rounded-lg">
        <p className="text-xs text-gray-500 mb-2">Sentiment overview</p>
        <div className="flex rounded-full overflow-hidden h-3 mb-2">
          <div className="bg-emerald-500" style={{ width: `${(positive / total) * 100}%` }} />
          <div className="bg-red-500" style={{ width: `${(negative / total) * 100}%` }} />
          <div className="bg-gray-600" style={{ width: `${(neutral / total) * 100}%` }} />
        </div>
        <div className="flex gap-4 text-xs">
          <span className="text-emerald-400">Positive {positive}%</span>
          <span className="text-red-400">Negative {negative}%</span>
          <span className="text-gray-400">Neutral {neutral}%</span>
        </div>
      </div>

      {/* Locked */}
      <div className="space-y-2 pt-2">
        <p className="text-xs text-gray-500 uppercase tracking-widest font-mono">Locked analysis</p>
        <LockedRow label={`View all ${data.total_posts.toLocaleString()} posts`} />
        <LockedRow label="Author analysis — top contributors and influence" />
        <LockedRow label="Narrative clusters and coordinated activity" />
      </div>
    </div>
  )
}

function BreachPanel({ data }: { data: BreachData }) {
  const maskedEmail = data.email.replace(/^(.)(.*)(@.*)$/, (_, first, mid, domain) => {
    return `${first}${'*'.repeat(Math.min(mid.length, 3))}${domain}`
  })

  return (
    <div className="space-y-4">
      {/* Summary */}
      <div className="p-4 bg-red-500/5 border border-red-500/20 rounded-lg">
        <div className="flex items-center gap-2 mb-3">
          <AlertTriangle className="w-4 h-4 text-red-400 flex-shrink-0" />
          <span className="text-red-300 font-semibold text-sm">Breach exposure detected</span>
        </div>
        <p className="text-gray-400 text-sm font-mono mb-2">{maskedEmail}</p>
        <div className="grid grid-cols-2 gap-3">
          <div className="p-2 bg-gray-800/60 rounded-lg">
            <p className="text-xs text-gray-500">Breaches found</p>
            <p className="text-red-300 font-bold text-xl">{data.breach_count}</p>
          </div>
          <div className="p-2 bg-gray-800/60 rounded-lg">
            <p className="text-xs text-gray-500">Records exposed</p>
            <p className="text-orange-300 font-bold text-xl">
              {data.total_records >= 1_000_000
                ? `${(data.total_records / 1_000_000).toFixed(1)}M`
                : data.total_records.toLocaleString()}
            </p>
          </div>
        </div>
      </div>

      {/* Data types */}
      <div className="p-3 bg-gray-800/40 border border-gray-700/50 rounded-lg">
        <p className="text-xs text-gray-500 mb-2 flex items-center gap-1">
          <Database className="w-3 h-3" /> Data types exposed
        </p>
        <div className="flex gap-2 flex-wrap">
          {data.data_types.map((t) => (
            <span key={t} className="px-2 py-0.5 bg-orange-500/10 border border-orange-500/20 rounded text-xs text-orange-300 capitalize">
              {t}
            </span>
          ))}
        </div>
      </div>

      {/* Locked breach rows */}
      <div className="space-y-2">
        <p className="text-xs text-gray-500 uppercase tracking-widest font-mono">Breach details (locked)</p>
        {Array.from({ length: Math.min(data.breach_count, 3) }).map((_, i) => (
          <div key={i} className="flex items-center gap-3 p-3 bg-gray-800/30 border border-gray-700/40 rounded-lg">
            <Lock className="w-3.5 h-3.5 text-gray-600 flex-shrink-0" />
            <div className="flex-1 h-3 bg-gray-700/60 rounded blur-sm" />
            <div className="flex gap-2">
              <div className="w-14 h-3 bg-gray-700/40 rounded blur-sm" />
              <div className="w-14 h-3 bg-gray-700/40 rounded blur-sm" />
            </div>
          </div>
        ))}
        {data.breach_count_hidden > 0 && (
          <p className="text-xs text-gray-600 text-center pt-1">
            {data.breach_count_hidden} more breach{data.breach_count_hidden === 1 ? '' : 'es'} hidden
          </p>
        )}
      </div>
    </div>
  )
}

function UpgradeCard({ previewId }: { previewId: string }) {
  return (
    <div className="sticky top-6 space-y-4">
      {/* Free plan */}
      <div className="bg-gray-900/80 border border-gray-700 rounded-xl p-5">
        <div className="flex items-center gap-2 mb-3">
          <span className="px-2 py-0.5 bg-emerald-500/20 border border-emerald-500/30 rounded text-xs text-emerald-400 font-semibold">
            Free Plan
          </span>
        </div>
        <ul className="space-y-2 mb-4">
          {['5 free credits', 'Basic breach lookup', 'Social search (limited)', 'X profile preview'].map((item) => (
            <li key={item} className="flex items-center gap-2 text-sm text-gray-300">
              <Check className="w-3.5 h-3.5 text-emerald-400 flex-shrink-0" />
              {item}
            </li>
          ))}
        </ul>
        <Link
          to={`/register?preview=${previewId}`}
          className="flex items-center justify-center gap-2 w-full py-2.5 px-4 bg-emerald-500 hover:bg-emerald-600 text-black font-semibold rounded-lg transition-colors text-sm"
        >
          Create Free Account
          <ArrowRight className="w-4 h-4" />
        </Link>
      </div>

      {/* Divider */}
      <div className="flex items-center gap-3">
        <div className="flex-1 h-px bg-gray-700" />
        <span className="text-xs text-gray-600">or</span>
        <div className="flex-1 h-px bg-gray-700" />
      </div>

      {/* Starter plan */}
      <div className="bg-gray-900/80 border border-gray-600 rounded-xl p-5">
        <div className="flex items-center gap-2 mb-1">
          <span className="text-white font-semibold text-sm">Starter Plan</span>
          <span className="text-gray-400 text-sm">— $29/mo</span>
        </div>
        <ul className="space-y-2 mb-4 mt-3">
          {[
            '100 credits/month',
            'Full BreachINT access',
            'WHOIS + Caller ID',
            'X Investigation',
            'Social stream search',
          ].map((item) => (
            <li key={item} className="flex items-center gap-2 text-sm text-gray-300">
              <Check className="w-3.5 h-3.5 text-blue-400 flex-shrink-0" />
              {item}
            </li>
          ))}
        </ul>
        <Link
          to="/pricing"
          className="flex items-center justify-center gap-2 w-full py-2.5 px-4 bg-gray-700 hover:bg-gray-600 text-white font-semibold rounded-lg transition-colors text-sm border border-gray-600"
        >
          Start for $29/mo
          <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
    </div>
  )
}

function LoadingSkeleton() {
  return (
    <div className="space-y-4">
      <SkeletonBlock className="h-16 w-full" />
      <div className="grid grid-cols-2 gap-3">
        <SkeletonBlock className="h-14" />
        <SkeletonBlock className="h-14" />
        <SkeletonBlock className="h-14" />
        <SkeletonBlock className="h-14" />
      </div>
      <SkeletonBlock className="h-24 w-full" />
      <SkeletonBlock className="h-12 w-full" />
      <SkeletonBlock className="h-12 w-full" />
    </div>
  )
}

export default function PreviewResults() {
  const [searchParams] = useSearchParams()
  const previewId = searchParams.get('id') ?? ''

  const [data, setData] = useState<PreviewData | null>(null)
  const [loading, setLoading] = useState(true)
  const [expired, setExpired] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    if (!previewId) {
      setExpired(true)
      setLoading(false)
      return
    }
    async function loadPreview() {
      try {
        const res = await fetch(`${API_BASE}/preview/${previewId}`)
        if (res.status === 404) {
          setExpired(true)
          return
        }
        if (!res.ok) {
          const body = await res.json().catch(() => ({}))
          throw new Error(body.detail ?? `Error ${res.status}`)
        }
        const json: PreviewData = await res.json()
        setData(json)
      } catch (err: unknown) {
        setError(err instanceof Error ? err.message : 'Failed to load preview. Please try again.')
      } finally {
        setLoading(false)
      }
    }
    loadPreview()
  }, [previewId])

  const moduleLabel: Record<Module, string> = {
    x: 'X Profile Analysis',
    social: 'Social Listener Report',
    breach: 'Data Breach Report',
  }

  const moduleIcon: Record<Module, ReactNode> = {
    x: <Twitter className="w-4 h-4 text-blue-400" />,
    social: <Users className="w-4 h-4 text-emerald-400" />,
    breach: <Shield className="w-4 h-4 text-red-400" />,
  }

  return (
    <div className="min-h-screen bg-[#0a0f0a] text-white">
      {/* Header */}
      <header className="border-b border-white/[0.06] bg-black/40 backdrop-blur-sm">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-7 h-7 bg-emerald-500 rounded flex items-center justify-center">
              <Shield className="w-4 h-4 text-black" />
            </div>
            <span className="text-white font-bold text-lg tracking-tight">SYCEK</span>
          </Link>
          <span className="px-2.5 py-1 bg-emerald-500/10 border border-emerald-500/30 rounded-full text-xs text-emerald-400 font-medium">
            Free Preview Report
          </span>
        </div>
      </header>

      <main className="container mx-auto px-4 py-10 max-w-5xl">
        {/* States */}
        {expired && (
          <div className="flex flex-col items-center justify-center py-24 text-center space-y-4">
            <Clock className="w-12 h-12 text-gray-600" />
            <h2 className="text-xl font-semibold text-white">This preview has expired</h2>
            <p className="text-gray-400 max-w-sm text-sm">
              Preview results are available for 24 hours. Run a new search to generate a fresh report.
            </p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 mt-2 px-5 py-2.5 bg-emerald-500 hover:bg-emerald-600 text-black font-semibold rounded-lg text-sm transition-colors"
            >
              <ArrowRight className="w-4 h-4" />
              Run a new search
            </Link>
          </div>
        )}

        {error && !expired && (
          <div className="flex flex-col items-center justify-center py-24 text-center space-y-3">
            <AlertTriangle className="w-10 h-10 text-red-400" />
            <p className="text-red-300 text-sm">{error}</p>
            <Link to="/" className="text-sm text-emerald-400 hover:underline">
              Back to search
            </Link>
          </div>
        )}

        {!expired && !error && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Results panel */}
            <div className="lg:col-span-2 space-y-4">
              {/* Module label */}
              {!loading && data && (
                <div className="flex items-center gap-2 mb-2">
                  {moduleIcon[data.module]}
                  <h2 className="text-white font-semibold text-lg">{moduleLabel[data.module]}</h2>
                </div>
              )}

              {/* Summary banner */}
              {!loading && data && (
                <div className="flex items-start gap-3 p-3 bg-emerald-500/10 border border-emerald-500/30 rounded-lg">
                  <Database className="w-4 h-4 text-emerald-400 mt-0.5 flex-shrink-0" />
                  <p className="text-emerald-300 text-sm font-medium">{data.summary}</p>
                </div>
              )}

              {/* Content */}
              {loading ? (
                <LoadingSkeleton />
              ) : data?.module === 'x' && data.x_profile ? (
                <XProfilePanel data={data.x_profile} />
              ) : data?.module === 'social' && data.social ? (
                <SocialPanel data={data.social} />
              ) : data?.module === 'breach' && data.breach ? (
                <BreachPanel data={data.breach} />
              ) : null}
            </div>

            {/* CTA sidebar */}
            <div className="lg:col-span-1">
              {loading ? (
                <div className="space-y-3">
                  <SkeletonBlock className="h-48" />
                  <SkeletonBlock className="h-4 w-24 mx-auto" />
                  <SkeletonBlock className="h-48" />
                </div>
              ) : (
                <UpgradeCard previewId={previewId} />
              )}
            </div>
          </div>
        )}
      </main>
    </div>
  )
}
